/**
 * O'Copro — Splash Screen
 * 
 * Fond vert émeraude (#00a85b) fidèle à la charte.
 * Animation séquentielle : logo → lettres O'Copro une par une → 3 dots.
 * 
 * Props:
 *  - isReady: boolean — true quand l'app est prête (auth initialisée)
 *  - onFinished: () => void — callback quand le splash est complètement fermé
 *  - minDuration: number — durée minimum d'affichage en ms (défaut: 2400)
 * 
 * Le logo doit être dans /public/logo1922.png
 */

import { useState, useEffect, useRef } from 'react';

// ✅ Vert émeraude exact extrait du logo officiel O'Copro
const BRAND = {
  emerald: '#00a85b',
  emeraldDark: '#008c4a',
  emeraldLight: '#2cc76f',
  white: '#ffffff',
};

const LOGO_SRC = '/logo1922.png';
const BRAND_NAME = "O'Copro";

interface SplashScreenProps {
  isReady?: boolean;
  onFinished?: () => void;
  minDuration?: number;
}

export const SplashScreen = ({
  isReady = false,
  onFinished,
  minDuration = 2400,
}: SplashScreenProps) => {
  const [phase, setPhase] = useState<'enter' | 'visible' | 'exit'>('enter');
  const [logoVisible, setLogoVisible] = useState(false);
  const [visibleLetters, setVisibleLetters] = useState(0);
  const [dotsVisible, setDotsVisible] = useState(false);
  const minElapsedRef = useRef(false);
  const readyRef = useRef(isReady);

  readyRef.current = isReady;

  // ── Séquence d'animation ──
  useEffect(() => {
    const timers: ReturnType<typeof setTimeout>[] = [];

    // 1) Logo apparaît
    timers.push(setTimeout(() => {
      setPhase('visible');
      setLogoVisible(true);
    }, 80));

    // 2) Lettres une par une (début après 650ms, chaque lettre +90ms)
    const letterStart = 650;
    const letterInterval = 90;
    for (let i = 0; i < BRAND_NAME.length; i++) {
      timers.push(setTimeout(() => {
        setVisibleLetters(i + 1);
      }, letterStart + i * letterInterval));
    }

    // 3) Dots apparaissent après la dernière lettre
    const dotsStart = letterStart + BRAND_NAME.length * letterInterval + 200;
    timers.push(setTimeout(() => setDotsVisible(true), dotsStart));

    return () => timers.forEach(clearTimeout);
  }, []);

  // ── Min duration ──
  useEffect(() => {
    const t = setTimeout(() => {
      minElapsedRef.current = true;
      if (readyRef.current) setPhase('exit');
    }, minDuration);
    return () => clearTimeout(t);
  }, [minDuration]);

  // ── isReady → exit ──
  useEffect(() => {
    if (isReady && minElapsedRef.current) setPhase('exit');
  }, [isReady]);

  // ── Exit → onFinished ──
  useEffect(() => {
    if (phase === 'exit') {
      const t = setTimeout(() => onFinished?.(), 800);
      return () => clearTimeout(t);
    }
  }, [phase, onFinished]);

  const exit = phase === 'exit';

  return (
    <>
      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=Nunito:wght@700;800&display=swap');

        @keyframes ocoproDotPulse {
          0%, 100% { opacity: 0.3; transform: scale(0.75); }
          50% { opacity: 1; transform: scale(1.15); }
        }

        @keyframes ocoproGlow {
          0%, 100% { opacity: 0.25; }
          50% { opacity: 0.5; }
        }

        @keyframes ocoproLetterIn {
          0% { opacity: 0; transform: translateY(12px) scale(0.85); }
          60% { opacity: 1; transform: translateY(-2px) scale(1.02); }
          100% { opacity: 1; transform: translateY(0) scale(1); }
        }
      `}</style>

      <div
        style={{
          position: 'fixed',
          inset: 0,
          zIndex: 99999,
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          background: BRAND.emerald,
          opacity: exit ? 0 : 1,
          transition: 'opacity 0.8s cubic-bezier(0.4, 0, 0.2, 1)',
          overflow: 'hidden',
        }}
      >
        {/* Ambient glow — haut droite */}
        <div
          style={{
            position: 'absolute',
            width: 450,
            height: 450,
            borderRadius: '50%',
            background: `radial-gradient(circle, ${BRAND.emeraldLight}28 0%, transparent 65%)`,
            top: '-14%',
            right: '-10%',
            animation: 'ocoproGlow 5s ease-in-out infinite',
            pointerEvents: 'none',
          }}
        />

        {/* Ambient glow — bas gauche */}
        <div
          style={{
            position: 'absolute',
            width: 380,
            height: 380,
            borderRadius: '50%',
            background: `radial-gradient(circle, ${BRAND.emeraldDark}35 0%, transparent 60%)`,
            bottom: '-10%',
            left: '-8%',
            animation: 'ocoproGlow 5s ease-in-out infinite 2.5s',
            pointerEvents: 'none',
          }}
        />

        {/* ── Logo icône ── */}
        <div
          style={{
            transform: logoVisible ? 'scale(1)' : 'scale(0.5)',
            opacity: logoVisible ? 1 : 0,
            transition: 'all 0.9s cubic-bezier(0.34, 1.56, 0.64, 1)',
            marginBottom: 28,
            filter: 'drop-shadow(0 8px 28px rgba(0, 0, 0, 0.12))',
          }}
        >
          <img
            src={LOGO_SRC}
            alt="O'Copro"
            style={{
              width: 110,
              height: 110,
              objectFit: 'contain',
            }}
          />
        </div>

        {/* ── Lettres O'Copro une par une ── */}
        <div
          style={{
            display: 'flex',
            alignItems: 'baseline',
            height: 44,
          }}
        >
          {BRAND_NAME.split('').map((char, i) => (
            <span
              key={i}
              style={{
                fontFamily: "'Nunito', sans-serif",
                fontWeight: 800,
                fontSize: 38,
                color: BRAND.white,
                letterSpacing: char === "'" ? '0px' : '-0.5px',
                display: 'inline-block',
                opacity: i < visibleLetters ? 1 : 0,
                animation: i < visibleLetters ? 'ocoproLetterIn 0.4s cubic-bezier(0.22, 1, 0.36, 1) forwards' : 'none',
              }}
            >
              {char}
            </span>
          ))}
        </div>

        {/* ── 3 dots blancs ── */}
        <div
          style={{
            marginTop: 44,
            display: 'flex',
            gap: 8,
            opacity: dotsVisible ? 1 : 0,
            transition: 'opacity 0.4s ease',
          }}
        >
          {[0, 1, 2].map((i) => (
            <div
              key={i}
              style={{
                width: 7,
                height: 7,
                borderRadius: '50%',
                background: BRAND.white,
                animation: dotsVisible
                  ? `ocoproDotPulse 1.3s ease-in-out infinite ${i * 0.2}s`
                  : 'none',
              }}
            />
          ))}
        </div>
      </div>
    </>
  );
};

export default SplashScreen;
